import { bundledQuran } from './source';

/**
 * A stored range is `{ from, to }`, NOT the pair `[from, to]` it reads more
 * naturally as: Firestore refuses an array containing another array, so the
 * pair form could never be stored at all. See functions/coverage.js.
 */
export type AyahRange = { from: number; to: number };

export type SurahCoverage = {
  ranges: AyahRange[];
  recited: number;
  errors: number;
  lastAt: number;
};

/**
 * How many DISTINCT ayahs a set of ranges holds.
 *
 * The server merges before it writes, so what is stored never overlaps — but
 * this counts the union rather than the sum anyway. A reader that trusts the
 * writer reports a percentage nobody can explain the day anything else writes
 * coverage, and 'ayahs covered' means the union in every other place it is
 * said out loud.
 */
export function ayahsIn(ranges: AyahRange[] | undefined): number {
  const sorted = [...(ranges ?? [])]
    .filter((r) => r && Number.isFinite(r.from) && Number.isFinite(r.to))
    .sort((a, b) => a.from - b.from);
  let total = 0;
  let reached = 0;   // the highest ayah already counted
  for (const { from, to } of sorted) {
    if (to <= reached) continue;
    total += to - Math.max(from - 1, reached);
    reached = to;
  }
  return total;
}

/**
 * A student's progress through one surah, for a bar the student reads.
 *
 * The same arithmetic the server does when it writes a standing — repeated
 * here because a client that waited for the server to tell it the percentage
 * of every surah would be a round trip per row of a list.
 */
export function surahProgress(
  coverage: Record<string, SurahCoverage>,
  surah: number
): { covered: number; total: number; percent: number; errors: number } {
  const meta = bundledQuran.surah(surah);
  const total = meta ? meta.ayahCount : 0;
  const held = coverage[String(surah)];
  const covered = Math.min(ayahsIn(held?.ranges), total);
  return {
    covered,
    total,
    percent: total === 0 ? 0 : Math.round((covered / total) * 100),
    errors: held?.errors ?? 0,
  };
}

/** The same, across every surah in a track. */
export function trackProgress(
  coverage: Record<string, SurahCoverage>,
  surahs: number[]
): { covered: number; total: number; percent: number; errors: number } {
  let covered = 0;
  let total = 0;
  let errors = 0;
  for (const surah of surahs) {
    const one = surahProgress(coverage, surah);
    covered += one.covered;
    total += one.total;
    errors += one.errors;
  }
  return { covered, total, percent: total === 0 ? 0 : Math.round((covered / total) * 100), errors };
}
